// src/context/BalanceContext.js

import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useContext,
  useRef,
  useMemo,
} from "react";
import { ethers } from "ethers";
import { WalletContext } from "./WalletContext";
import { toast } from "react-toastify";
import { loadTokenContract } from "../utils/loadBlockchain";
import { NETWORK_CONFIG } from "../contracts/constants";

export const BalanceContext = createContext();

const BALANCE_UPDATE_INTERVAL = 30000;

export const BalanceProvider = ({ children }) => {
  const { account, provider, chainId } = useContext(WalletContext);

  // State
  const [cashBalance, setCashBalance] = useState("0");
  const [nativeBalance, setNativeBalance] = useState("0");
  const [isInitializing, setIsInitializing] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs
  const tokenContractRef = useRef(null);
  const contractsInitializedRef = useRef(false);
  const lastUpdateRef = useRef({ cash: 0, native: 0 });

  // Helper for retrying contract calls
  const retryContractCall = useCallback(async (fn, maxAttempts = 3) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await fn();
      } catch (error) {
        if (
          error.code === -32603 ||
          error?.data?.code === -32094 ||
          (error?.data?.message || "").toLowerCase().includes("block is out of range")
        ) {
          if (i < maxAttempts - 1) {
            const delay = Math.pow(2, i) * 1000;
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
        }
        throw error;
      }
    }
    return null;
  }, []);

  const shouldUpdateBalance = (type) => {
    const now = Date.now();
    const lastUpdate = lastUpdateRef.current[type];
    return now - lastUpdate >= BALANCE_UPDATE_INTERVAL;
  };

  const fetchCashBalance = useCallback(
    async (force = false) => {
      const tokenContract = tokenContractRef.current;
      if (!tokenContract || !account || (!force && !shouldUpdateBalance("cash"))) return;
      try {
        const [rawBalance, decimals] = await Promise.all([
          retryContractCall(() => tokenContract.balanceOf(account)),
          retryContractCall(() => tokenContract.decimals()),
        ]);
        const formattedBalance = ethers.utils.formatUnits(rawBalance, decimals);
        setCashBalance(formattedBalance);
        lastUpdateRef.current.cash = Date.now();
      } catch (error) {
        const errMsg = (error.message || "").toLowerCase();
        if (errMsg.includes("block is out of range")) {
          return;
        }
        console.error("Error fetching token balance:", error);
        if (!errMsg.includes("user rejected") && error.code !== 4001) {
          toast.error("Failed to fetch token balance", {
            autoClose: 3000,
            hideProgressBar: true,
          });
        }
      }
    },
    [account, retryContractCall]
  );

  const fetchNativeBalance = useCallback(
    async (force = false) => {
      if (
        !provider ||
        !account ||
        (!force && !shouldUpdateBalance("native")) ||
        chainId !== NETWORK_CONFIG.chainId
      ) {
        return;
      }
      try {
        const rawBal = await retryContractCall(() => provider.getBalance(account));
        const formatted = ethers.utils.formatEther(rawBal);
        setNativeBalance(formatted);
        lastUpdateRef.current.native = Date.now();
      } catch (error) {
        console.error("Error fetching native balance:", error);
      }
    },
    [provider, account, chainId, retryContractCall]
  );

  // --- Initialize the token contract ---
  const initializeTokenContract = useCallback(async () => {
    if (!provider || !account || contractsInitializedRef.current || chainId !== NETWORK_CONFIG.chainId) {
      return;
    }
    try {
      setIsInitializing(true);
      const tokenContract = await loadTokenContract(provider);
      if (!tokenContract) {
        throw new Error("Token contract failed to load");
      }
      const code = await provider.getCode(tokenContract.address);
      if (code === "0x") {
        throw new Error(`Token contract not deployed on ${NETWORK_CONFIG.name}`);
      }
      tokenContractRef.current = tokenContract;
      contractsInitializedRef.current = true;
      setIsInitialized(true);
      await fetchCashBalance(true);
    } catch (error) {
      console.error("Failed to initialize token contract:", error);
      toast.error("Failed to initialize token contract: " + error.message, {
        autoClose: 5000,
        hideProgressBar: true,
      });
      setIsInitialized(false);
      contractsInitializedRef.current = false;
    } finally {
      setIsInitializing(false);
    }
  }, [provider, account, chainId, fetchCashBalance]);

  useEffect(() => {
    if (
      provider &&
      account &&
      !isInitializing &&
      !isInitialized &&
      chainId === NETWORK_CONFIG.chainId
    ) {
      initializeTokenContract();
    }
  }, [provider, account, isInitializing, isInitialized, chainId, initializeTokenContract]);

  useEffect(() => {
    if (!provider || !account || chainId !== NETWORK_CONFIG.chainId) {
      setCashBalance("0");
      setNativeBalance("0");
      tokenContractRef.current = null;
      contractsInitializedRef.current = false;
      setIsInitialized(false);
      setIsInitializing(false);
    }
  }, [provider, account, chainId]);

  useEffect(() => {
    if (!provider || !account || chainId !== NETWORK_CONFIG.chainId) return;
    const updateBalances = async () => {
      await Promise.all([fetchCashBalance(), fetchNativeBalance()]);
    };
    updateBalances();
    const intervalId = setInterval(updateBalances, BALANCE_UPDATE_INTERVAL);
    return () => clearInterval(intervalId);
  }, [provider, account, chainId, fetchCashBalance, fetchNativeBalance]);

  useEffect(() => {
    if (!provider || !account || chainId !== NETWORK_CONFIG.chainId) return;
    const handleBlock = async () => {
      await Promise.all([fetchCashBalance(), fetchNativeBalance()]);
    };
    provider.on("block", handleBlock);
    return () => provider.off("block", handleBlock);
  }, [provider, account, chainId, fetchCashBalance, fetchNativeBalance]);

  useEffect(() => {
    if (!provider || !account || chainId !== NETWORK_CONFIG.chainId) {
      setCashBalance("0");
      setNativeBalance("0");
      tokenContractRef.current = null;
      contractsInitializedRef.current = false;
      setIsInitialized(false);
      setIsInitializing(false);
    }
  }, [provider, account, chainId]);

  const value = useMemo(
    () => ({
      cashBalance,
      nativeBalance,
      isInitialized,
      isInitializing,
      fetchCashBalance: () => fetchCashBalance(true),
      fetchNativeBalance: () => fetchNativeBalance(true),
    }),
    [cashBalance, nativeBalance, isInitialized, isInitializing, fetchCashBalance, fetchNativeBalance]
  );

  return <BalanceContext.Provider value={value}>{children}</BalanceContext.Provider>;
};

export default BalanceProvider;